import { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/auth-context';
import { useLanguage } from '../contexts/language-context';
import { validateLoginForm, validateRegistrationForm } from '../validators';
import { CONSOLE_MESSAGES } from '../constants';
import { GOOGLE_CLIENT_ID } from '../config/app-config';
import { MODAL_CLASSES, ICON_CLASSES, ALERT_CLASSES, cn } from '../styles';

// Translations for the divider
const DIVIDER_TEXT = { lt: 'arba', en: 'or', uk: 'або' };

function AuthModal({ isOpen, onClose, initialMode = 'login', onRegistrationSuccess, onForgotPasswordClick }) {
  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, loginWithGoogle, register, isLoading, error, clearError } = useAuth();
  const { language } = useLanguage();

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (isOpen) {
      setFormData({ email: '', password: '', confirmPassword: '' });
      setErrors({});
      clearError?.();
    }
  }, [isOpen, clearError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const { isValid, errors: validationErrors } = mode === 'register' 
      ? validateRegistrationForm(formData)
      : validateLoginForm(formData);
    setErrors(validationErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (mode === 'login') {
        await login({
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
        });
        onClose();
      } else {
        await register({
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        });
        onRegistrationSuccess?.(formData.email.toLowerCase().trim()) || onClose();
      }
    } catch (err) {
      console.error(CONSOLE_MESSAGES.AUTH_ERROR, err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (response) => {
    setIsSubmitting(true);
    try {
      await loginWithGoogle(response.credential);
      onClose();
    } catch (err) {
      console.error(CONSOLE_MESSAGES.AUTH_ERROR, err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className={MODAL_CLASSES.OVERLAY} onClick={handleOverlayClick}>
      <div className="relative w-full max-w-md bg-dark-800 rounded-xl sm:rounded-2xl shadow-2xl border border-dark-700 overflow-hidden max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          aria-label="Uždaryti"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="p-4 sm:p-6 pb-3 sm:pb-4">
          <div className="text-center">
            <div className={cn(ICON_CLASSES.CONTAINER_LG, ICON_CLASSES.PRIMARY_GRADIENT, 'mx-auto mb-3 sm:mb-4')}>
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14 16a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2h8z" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1.5 sm:mb-2">
              {mode === 'login' ? 'Prisijungti' : 'Registruotis'}
            </h2>
            <p className="text-gray-400 text-xs sm:text-sm">
              {mode === 'login' ? 'Prisijunkite prie savo paskyros' : 'Sukurkite naują paskyrą'}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-3 sm:space-y-4">
          {error && <div className={ALERT_CLASSES.ERROR}>{error}</div>}

          {/* Email */}
          <div className="space-y-1.5 sm:space-y-2">
            <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-300">
              El. paštas
            </label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isSubmitting}
              className={`input-primary w-full text-sm sm:text-base ${errors.email ? 'border-red-500 focus:border-red-500' : ''}`}
              placeholder="Įveskite el. pašto adresą"
            />
            {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
          </div>

          {/* Password */}
          <div className="space-y-1.5 sm:space-y-2">
            <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-300">
              Slaptažodis
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={formData.password}
                onChange={handleInputChange}
                disabled={isSubmitting}
                className={`input-primary w-full pr-10 sm:pr-12 text-sm sm:text-base ${errors.password ? 'border-red-500 focus:border-red-500' : ''}`}
                placeholder={mode === 'login' ? 'Įveskite slaptažodį' : 'Sukurkite slaptažodį'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
                className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M9.878 9.878l4.242 4.242M14.12 14.12l1.415 1.415M14.12 14.12l4.242 4.242M14.12 14.12L19.85 19.85" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <p className="text-red-400 text-xs">{errors.password}</p>}
            {mode === 'register' && (
              <p className="text-gray-400 text-[11px] sm:text-xs mt-1 leading-tight">
                Slaptažodis turi turėti bent 8 simbolius, didžiąją raidę, mažąją raidę ir skaičių
              </p>
            )}
            {mode === 'login' && onForgotPasswordClick && (
              <div className="text-right mt-1">
                <button
                  type="button"
                  onClick={onForgotPasswordClick}
                  disabled={isSubmitting}
                  className="text-primary-400 hover:text-primary-300 text-xs transition-colors disabled:opacity-50"
                >
                  Pamiršote slaptažodį?
                </button>
              </div>
            )}
          </div>

          {/* Confirm Password (register only) */}
          {mode === 'register' && (
            <div className="space-y-1.5 sm:space-y-2">
              <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-300">
                Patvirtinti slaptažodį
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className={`input-primary w-full pr-10 sm:pr-12 text-sm sm:text-base ${errors.confirmPassword ? 'border-red-500 focus:border-red-500' : ''}`}
                  placeholder="Pakartokite slaptažodį"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isSubmitting}
                  className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M9.878 9.878l4.242 4.242M14.12 14.12l1.415 1.415M14.12 14.12l4.242 4.242M14.12 14.12L19.85 19.85" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-red-400 text-xs">{errors.confirmPassword}</p>}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="btn-primary w-full justify-center relative disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base h-11 sm:h-12"
          >
            {isSubmitting || isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {mode === 'login' ? 'Jungiamasi...' : 'Registruojamasi...'}
              </>
            ) : (
              mode === 'login' ? 'Prisijungti' : 'Registruotis'
            )}
          </button>

          {/* Google Sign-In */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div className="relative my-3 sm:my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-dark-700"></div>
                </div>
                <div className="relative flex justify-center text-xs sm:text-sm">
                  <span className="px-2 sm:px-3 bg-dark-800 text-gray-400">
                    {DIVIDER_TEXT[language] || DIVIDER_TEXT.lt}
                  </span>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="w-full max-w-[300px]">
                  <GoogleLogin
                    key={`google-${language}`}
                    onSuccess={handleGoogleSuccess}
                    onError={() => console.error('Google Sign-In failed')}
                    theme="filled_black"
                    shape="rectangular"
                    size="large"
                    text={mode === 'login' ? 'signin_with' : 'signup_with'}
                    locale={language}
                    width="100%"
                    containerProps={{ style: { width: '100%' } }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Mode switch */}
          <div className="text-center pt-3 sm:pt-4 border-t border-dark-700">
            <p className="text-gray-400 text-xs sm:text-sm">
              {mode === 'login' ? 'Neturite paskyros?' : 'Jau turite paskyrą?'}
              {' '}
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                disabled={isSubmitting}
                className="text-primary-400 hover:text-primary-300 font-medium transition-colors disabled:opacity-50"
              >
                {mode === 'login' ? 'Registruotis' : 'Prisijungti'}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AuthModal;
